import { useState, useEffect } from 'react';
import { LoanOffer, HeatmapCell, Collection } from '../types';
import * as d3 from 'd3';
import { NFTFI_COLLECTION_NAME_MAP } from '../utils/nftfiCollectionNames';

const MARKET_OFFERS_TOKEN = 'p.eyJ1IjogImQzNzk4ZmNmLWIyYmUtNDk2MS04NzNiLWViYWJiM2ZhOGVmNyIsICJpZCI6ICJiYjQ2MGNjZC1jZWZkLTRmNmUtOTRhYS0zMDA1NDBjMTFmNTQiLCAiaG9zdCI6ICJ1c19lYXN0In0.0h_OPHPaO7PRIxbuwTHTI31ixLEssoEwWRhesdWbkBk';
const GONDI_OFFERS_TOKEN = 'p.eyJ1IjogImQzNzk4ZmNmLWIyYmUtNDk2MS04NzNiLWViYWJiM2ZhOGVmNyIsICJpZCI6ICJhMDg1MWQ4NC1iYmQ1LTQ3MDgtYjNkMS00Zjg5MTNlODIyZWUiLCAiaG9zdCI6ICJ1c19lYXN0In0.XOrSozxbRVrI1p2bgC9ZM8xyv_2D6MURCuZtzLhgBHY';
const COLLECTION_ADDRESS = '0xb7f7f6c52f2e2fdb1963eab30438024864c313f6';

export type Currency = 'WETH' | 'USDC';

// Update the LoanOffer type to include the type field
interface LoanOfferWithType extends LoanOffer {
  type: 'loan_offer' | 'active_loan';
}

// Helper to normalize a Gondi offer
function normalizeGondiOffer(offer: any): LoanOffer & { createdAt: string; timestamp: number } {
  const createdAt = offer.createddate;
  const timestamp = Date.parse(createdAt);
  if (isNaN(timestamp)) {
    console.warn('Gondi offer has unparseable createddate:', offer);
    throw new Error('Gondi offer missing or invalid createddate');
  }
  const decimals = offer.currency_decimals ?? (offer.currency_symbol === 'WETH' ? 18 : 6);
  const loanAmount = offer.principalamount / Math.pow(10, decimals);
  const fxRateToUSD = offer.fxRateToUSD ?? (offer.currency_symbol === 'USDC' ? 1 : 0);
  return {
    id: offer.id,
    loanAmount,
    interestRate: offer.aprbps / 100,
    duration: offer.duration / 86400,
    collection: offer.collectionname,
    lender: (offer.lenderaddress || '').toLowerCase(),
    currency: offer.currency_symbol,
    currencySymbol: offer.currency_symbol,
    fxRateToUSD,
    loanPrincipalUSD: offer.currency_symbol === 'WETH' ? loanAmount * fxRateToUSD : loanAmount,
    createdAt,
    timestamp,
  };
}

// Helper to normalize a Market offer
function normalizeMarketOffer(offer: any): LoanOffer & { createdAt: string; timestamp: number } {
  const createdAt = offer.offerDate;
  const timestamp = Date.parse(createdAt);
  if (isNaN(timestamp)) {
    console.warn('Market offer has unparseable offerDate:', offer);
    throw new Error('Market offer missing or invalid offerDate');
  }
  const fxRateToUSD = offer.fxRateToUSD ?? (offer.currencySymbol === 'USDC' ? 1 : 0);
  const loanAmount = offer.loanPrincipalCanonicalAmount;
  return {
    id: offer._id,
    loanAmount,
    interestRate: offer.eAPR ?? offer.apr,
    duration: offer.loanDuration / 86400,
    collection: offer.collectionName,
    lender: (offer.lender || '').toLowerCase(),
    currency: offer.currencySymbol,
    currencySymbol: offer.currencySymbol,
    fxRateToUSD,
    loanPrincipalUSD: offer.currencySymbol === 'WETH' ? loanAmount * fxRateToUSD : loanAmount,
    createdAt,
    timestamp,
  };
}

// Helper to normalize an active loan
function normalizeActiveLoan(loan: any): LoanOffer & { createdAt: string; timestamp: number } {
  const createdAt = loan.startTime;
  const timestamp = Date.parse(createdAt);
  if (isNaN(timestamp)) {
    console.warn('Active loan has unparseable startTime:', loan);
    throw new Error('Active loan missing or invalid startTime');
  }
  
  // Convert currency name to match existing format (e.g., 'wETH' to 'WETH')
  const currency = loan.currencyName === 'wETH' ? 'WETH' : loan.currencyName;
  
  return {
    id: loan.loanId,
    loanAmount: loan.principalAmount,
    interestRate: loan.apr,
    duration: loan.durationDays,
    collection: loan.nftProjectName,
    lender: (loan.lenderAddress || '').toLowerCase(),
    currency,
    currencySymbol: currency,
    fxRateToUSD: loan.principalAmountUSD / loan.principalAmount,
    loanPrincipalUSD: loan.principalAmountUSD,
    createdAt,
    timestamp,
  };
}

export const useLoanOffers = (collectionAddress?: string, collections: Collection[] = []) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedCurrency, setSelectedCurrency] = useState<'WETH' | 'USDC'>('WETH');
  const [allLoanOffers, setAllLoanOffers] = useState<LoanOfferWithType[]>([]);
  const [heatmap, setHeatmap] = useState<HeatmapCell[]>([]);
  const [allMarketOffers, setAllMarketOffers] = useState<any[]>([]);
  const [allGondiOffers, setAllGondiOffers] = useState<any[]>([]);
  const [activeLoans, setActiveLoans] = useState<LoanOffer[]>([]);

  // Fetch both APIs once on mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Clear active loans immediately to prevent stale display
        setActiveLoans([]);

        // First fetch market offers to get collection name
        const marketRes = await fetch(`https://api.us-east.tinybird.co/v0/pipes/market_offers_pipe.json?token=${MARKET_OFFERS_TOKEN}&collection_address=${collectionAddress || COLLECTION_ADDRESS}`);
        if (!marketRes.ok) throw new Error('Failed to fetch market offers');
        const marketData = await marketRes.json();

        // Find collection name from market offers
        let collectionName = undefined;
        if (marketData.data && marketData.data.length > 0) {
          const found = marketData.data.find((o: any) => o.collectionName && o.collectionName.length > 0);
          if (found) collectionName = found.collectionName;
        }
        // Use the mapped NFTfi collection name if available
        let activeLoansCollectionName;
        if (collectionAddress && NFTFI_COLLECTION_NAME_MAP[collectionAddress.toLowerCase()]) {
          activeLoansCollectionName = NFTFI_COLLECTION_NAME_MAP[collectionAddress.toLowerCase()];
          console.log('[useLoanOffers] Using mapped NFTfi collection name:', activeLoansCollectionName);
        } else if (collectionAddress && collections.length > 0) {
          const foundCollection = collections.find(c => c.contract_address === collectionAddress);
          activeLoansCollectionName = foundCollection?.name;
          console.log('[useLoanOffers] Resolved collection name for NFTfi:', activeLoansCollectionName, 'from address:', collectionAddress);
        }
        if (!activeLoansCollectionName) {
          activeLoansCollectionName = collectionName || 'Bored Ape Yacht Club';
          console.log('[useLoanOffers] Fallback collection name for NFTfi:', activeLoansCollectionName);
        }
        const encodedCollectionName = encodeURIComponent(activeLoansCollectionName);

        // Now fetch gondi offers and active loans with the collection name
        const [gondiRes, activeLoansRes] = await Promise.all([
          fetch(`https://api.us-east.tinybird.co/v0/pipes/gondi_offers_pipe.json?token=${GONDI_OFFERS_TOKEN}&limit=1000`),
          fetch(`https://sdk-api.nftfi.com/data/v0/pipes/loans_due_endpoint.json?nftProjectNames=${encodedCollectionName}&daysFromNow=30&page_size=1000000&page=0&sort_by=secondsUntilDue&sort_order=ASC`)
        ]);
        if (!gondiRes.ok || !activeLoansRes.ok) throw new Error('Failed to fetch offers');
        const [gondiData, activeLoansData] = await Promise.all([gondiRes.json(), activeLoansRes.json()]);

        // Prepare market offers for charting
        const marketOffers: any[] = (marketData.data || []);
        const marketOffersForChart = marketOffers.map(normalizeMarketOffer);
        setAllMarketOffers(marketOffersForChart);

        // Prepare Gondi offers for heatmap
        setAllGondiOffers(gondiData.data || []);

        // Prepare active loans
        const activeLoansForChart = (activeLoansData.data || []).map(normalizeActiveLoan);
        setActiveLoans(activeLoansForChart);

        setError(null);
      } catch (err) {
        console.error('Error fetching loan offers:', err);
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [collectionAddress]);

  // Filter and compute chart/heatmap data in-memory on currency toggle
  useEffect(() => {
    // Filter market offers for selected currency
    const filteredOffers = allMarketOffers.filter((offer: LoanOffer & { currencySymbol?: string }) =>
      offer.currency === selectedCurrency || offer.currencySymbol === selectedCurrency
    );

    // Filter active loans for selected currency
    const filteredActiveLoans = activeLoans.filter((loan: LoanOffer) =>
      loan.currency === selectedCurrency
    );

    // Combine all data points into one array with type flag
    const allDataPoints = [
      ...filteredOffers.map(o => ({ ...o, type: 'loan_offer' as const })),
      ...filteredActiveLoans.map(o => ({ ...o, type: 'active_loan' as const }))
    ];

    // Compute depth for all data points
    const depthMap: Record<string, number> = {};
    allDataPoints.forEach(point => {
      const key = [point.loanAmount, point.interestRate, point.duration, point.currency].join('|');
      depthMap[key] = (depthMap[key] || 0) + 1;
    });

    // Add depth to all data points
    const dataPointsWithDepth = allDataPoints.map(point => ({
      ...point,
      depth: depthMap[[point.loanAmount, point.interestRate, point.duration, point.currency].join('|')]
    }));

    setAllLoanOffers(dataPointsWithDepth);

    // Normalize Gondi offers for the heatmap
    const gondiOffersNorm = allGondiOffers.map(normalizeGondiOffer);
    const offersForHeatmap = gondiOffersNorm.filter((o: LoanOffer) => o.currency === selectedCurrency);

    // Square grid: use same number of bins for x and y
    const numBins = 20;
    const xExtentRaw = d3.extent(offersForHeatmap, (o: LoanOffer) => o.loanAmount);
    const yExtentRaw = d3.extent(offersForHeatmap, (o: LoanOffer) => o.interestRate);
    const xExtent: [number, number] = [
      typeof xExtentRaw[0] === 'number' ? xExtentRaw[0] : 0,
      typeof xExtentRaw[1] === 'number' ? xExtentRaw[1] : 1
    ];
    const yExtent: [number, number] = [
      typeof yExtentRaw[0] === 'number' ? yExtentRaw[0] : 0,
      typeof yExtentRaw[1] === 'number' ? yExtentRaw[1] : 1
    ];
    const xScale = d3.scaleLinear().domain(xExtent).nice().range([0, numBins]);
    const yScale = d3.scaleLinear().domain(yExtent).nice().range([0, numBins]);

    // Bin offers
    const bins: HeatmapCell[][] = Array.from({ length: numBins }, () =>
      Array.from({ length: numBins }, () => ({ count: 0, xBin: 0, yBin: 0, x0: 0, x1: 0, y0: 0, y1: 0 }))
    );
    offersForHeatmap.forEach((o: LoanOffer) => {
      const xBin = Math.min(numBins - 1, Math.max(0, Math.floor(xScale(o.loanAmount))));
      const yBin = Math.min(numBins - 1, Math.max(0, Math.floor(yScale(o.interestRate))));
      bins[xBin][yBin].count += 1;
      bins[xBin][yBin].xBin = xBin;
      bins[xBin][yBin].yBin = yBin;
    });
    // Set bin bounds
    for (let i = 0; i < numBins; i++) {
      for (let j = 0; j < numBins; j++) {
        bins[i][j].x0 = xScale.invert(i);
        bins[i][j].x1 = xScale.invert(i + 1);
        bins[i][j].y0 = yScale.invert(j);
        bins[i][j].y1 = yScale.invert(j + 1);
      }
    }
    // Flatten and filter empty
    const flatHeatmap = bins.flat().filter((cell: HeatmapCell) => cell.count > 0);
    setHeatmap(flatHeatmap);
  }, [allMarketOffers, allGondiOffers, activeLoans, selectedCurrency]);

  console.log('Returning from useLoanOffers:', {
    loanOffers: allLoanOffers.length,
    allLoanOffers: allMarketOffers.length,
    collections: collections.length,
    loading,
    error,
    selectedCurrency,
    heatmap: heatmap.length,
    activeLoans: activeLoans.length
  });

  return {
    loanOffers: allLoanOffers,
    allLoanOffers: allMarketOffers,
    collections,
    loading,
    error,
    selectedCurrency,
    setSelectedCurrency,
    heatmap,
    activeLoans
  };
}; 